import * as React from 'react';
import { StatusChip } from '@/components/ui/status-chip';
import { WorkspaceButton } from '@/components/ui/workspace';
import type { ProductInScope } from '@/lib/api/vendor-intelligence';
import { plural } from './vi-logic';

/**
 * Vendors in scope (T-0267; compact rows in T-0272, E24-design §2): one row
 * per product with its vendor, its confirmed item and answer counts in this
 * engagement's modules (the server's), the list it came from, and Remove.
 * Add vendors sits in the step's toolbar and opens the picker. Removing a
 * product keeps its ratings, hidden until it is added back.
 */
export function VendorsPart({
  products,
  canEdit,
  busy,
  onRemove,
}: {
  products: ProductInScope[];
  canEdit: boolean;
  busy: boolean;
  onRemove: (productId: string) => void;
}) {
  if (products.length === 0) {
    return (
      <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
        Choose at least two vendors: from one of the firm's vendor lists, or from all vendors in the
        knowledge base.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-card border border-ink-100">
      <table className="w-full border-collapse text-body-sm" aria-label="Vendors in scope">
        <thead>
          <tr className="border-b border-ink-100 text-left text-meta font-medium text-ink-500">
            <th scope="col" className="px-3 py-2 font-medium">
              Vendor
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Product
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Evidence in these modules
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              From
            </th>
            {canEdit ? (
              <th scope="col" className="px-3 py-2 font-medium">
                <span className="sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.product_id} className="border-b border-ink-100 last:border-b-0">
              <td className="px-3 py-2 text-ink-700">{p.vendor.name}</td>
              <td className="px-3 py-2">
                <span className="font-semibold text-ink-900">{p.name}</span>
                {p.status !== 'active' ? (
                  <StatusChip tone="frozen" size="sm" className="ml-2">
                    Retired
                  </StatusChip>
                ) : null}
              </td>
              <td className="px-3 py-2 tabular-nums text-ink-700">
                {plural(p.confirmed_items, 'confirmed item')} · {plural(p.answers, 'answer')}
              </td>
              <td className="px-3 py-2">
                <StatusChip tone="neutral" size="sm">
                  {p.from_list ? `From ${p.from_list.name}` : 'From all vendors'}
                </StatusChip>
              </td>
              {canEdit ? (
                <td className="px-2 py-1 text-right">
                  <WorkspaceButton
                    variant="tertiary"
                    disabled={busy}
                    aria-label={`Remove ${p.name}`}
                    onClick={() => onRemove(p.product_id)}
                  >
                    Remove
                  </WorkspaceButton>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
